import {useMemo} from "react";
import {ImportRow} from "./useContactImportParser";

export type ImportContact = {
    firstName: string;
    lastName: string;
    emailWork: string;
    emailPersonal: string;
    phoneMobileWork: string;
    phoneMobilePersonal: string;
    phoneHome: string;
    groups: string[];
};

// Maps CSV/XLSX header names to ImportContact fields.
const HEADER_MAP: Record<string, keyof Omit<ImportContact, "groups">> = {
    "First Name*":             "firstName",
    "Last Name*":              "lastName",
    "Email Address":           "emailWork",
    "Personal Email Address":  "emailPersonal",
    "Work Mobile Phone":       "phoneMobileWork",
    "Personal Mobile Phone":   "phoneMobilePersonal",
    "Home Phone":              "phoneHome",
};

export function useMapImportRows(headers: ImportRow, rows: ImportRow[]): ImportContact[] {
    return useMemo(() => {
        // Build index list for each field and collect all Group column indices.
        const fieldIndices: Partial<Record<keyof Omit<ImportContact, "groups">, number>> = {};
        const groupIndices: number[] = [];

        headers.forEach((h, i) => {
            const trimmed = h.trim();
            if (trimmed === "Group") {
                groupIndices.push(i);
            } else if (HEADER_MAP[trimmed]) {
                fieldIndices[HEADER_MAP[trimmed]] = i;
            }
        });

        return rows.map((row): ImportContact => {
            const get = (field: keyof Omit<ImportContact, "groups">) => {
                const idx = fieldIndices[field];
                return idx !== undefined ? (row[idx] ?? "").trim() : "";
            };

            const groups = groupIndices
                .map((i) => (row[i] ?? "").trim())
                .filter(Boolean);

            return {
                firstName:          get("firstName"),
                lastName:           get("lastName"),
                emailWork:          get("emailWork"),
                emailPersonal:      get("emailPersonal"),
                phoneMobileWork:    get("phoneMobileWork"),
                phoneMobilePersonal: get("phoneMobilePersonal"),
                phoneHome:          get("phoneHome"),
                groups,
            };
        }).filter((c) => c.firstName || c.lastName);
    }, [headers, rows]);
}
